import { io, Socket } from 'socket.io-client'
import { telephonyApi } from './api'
import { callMachine } from './callMachine'
import { describeDialFailure, readFailureCause, type FailureCategory } from './dialFailure'

export interface CallUpdatedEvent {
  callId: string
  tenantId: string
  userId: string | null
  twilioSid: string | null
  status: string
  outcome: string
  direction: string | null
  duration: number
  /**
   * Issue #144 (E-03) : cause d'échec quand le serveur la connaît. Un second
   * push `FAILED` peut l'apporter APRÈS le premier (le code arrive par un
   * autre webhook) : `refineDialFailure` l'absorbe sans rouvrir quoi que ce soit.
   */
  twilioErrorCode?: string | null
  failureCategory?: FailureCategory | null
}

export interface IncomingCallEvent {
  tenantId: string
  /**
   * Id du `Call` v2 créé par `upsertInbound`. Le gateway l'émet depuis la
   * Phase 2 ; le type frontend l'ignorait encore (écart inter-lots relevé à
   * l'audit du module assemblé).
   */
  callId: string
  twilioSid: string
  fromNumber: string
  contactId?: string
  contactName?: string
}

/**
 * Client WebSocket v2 — namespace /telephony.
 *
 * Le backend pousse `call.updated` après chaque écriture DB : la machine à
 * états avance sur ces événements, ce qui supprime tout le polling v1
 * (setInterval 1s/2s) et les retries `findRecentCall`.
 */
class TelephonySocket {
  private socket: Socket | null = null

  connect(params: { tenantId: string; userId: string }): void {
    // Une socket existante mais déconnectée doit être JETÉE avant d'en ouvrir
    // une autre : sans ça, chaque reconnexion manuelle laissait derrière elle
    // un client socket.io orphelin qui continuait ses propres tentatives.
    if (this.socket) {
      if (this.socket.connected) return
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }

    const apiUrl = import.meta.env.VITE_API_URL || window.location.origin
    // Ancré en fin de chaîne : ne pas amputer un domaine contenant "/api" ailleurs.
    const wsUrl = apiUrl.replace(/\/api\/?$/, '')

    this.socket = io(`${wsUrl}/telephony`, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      // ⚠️ `auth` en FONCTION, pas en objet figé : socket.io la rappelle à
      // CHAQUE (re)connexion. Avec un objet, le token lu au montage était
      // rejoué indéfiniment — après un réveil d'iPad et un refresh de token,
      // le handshake était refusé en boucle et l'agent ne recevait plus aucune
      // mise à jour d'appel, sans le moindre signe visible.
      auth: (cb: (data: { token: string }) => void) => {
        cb({ token: localStorage.getItem('accessToken') || '' })
      },
    })

    this.socket.on('connect', () => {
      this.socket?.emit('subscribe', params)
      // RATTRAPAGE après (re)connexion : pendant la coupure, des `call.updated`
      // ont pu être perdus. On relit l'état du seul appel qui nous intéresse —
      // une requête ponctuelle déclenchée par un ÉVÉNEMENT, jamais une boucle
      // de scrutation.
      void this.resyncCurrentCall()
    })

    this.socket.on('call.updated', (event: CallUpdatedEvent) => {
      this.applyCallUpdate(event)
    })

    this.socket.on('call.incoming', (_event: IncomingCallEvent) => {
      // La sonnerie côté SDK (device `incoming`) est la source primaire ;
      // cet événement sert aux UIs annexes (badge, notif multi-agents).
    })
  }

  /**
   * Fait avancer la machine sur une mise à jour serveur.
   *
   * Public pour être exercé sans socket réelle (harnais / diagnostic) : c'est
   * la seule logique de ce fichier qui mérite d'être testée.
   */
  applyCallUpdate(event: CallUpdatedEvent): void {
    const current = callMachine.getSnapshot()
    // Ne réagir qu'aux updates de NOTRE appel en cours.
    if (!current.callId || event.callId !== current.callId) return

    if (event.twilioSid && !current.twilioSid) {
      callMachine.attachTwilioSid(event.twilioSid)
    }

    switch (event.status) {
      case 'DIALING':
        // `remoteRinging` est idempotent : si le SDK a déjà annoncé la
        // sonnerie, ce push n'est qu'une confirmation (plus de « transition
        // refusée ringing→ringing » dans les logs).
        if (current.phase === 'dialing') callMachine.remoteRinging()
        break

      case 'IN_PROGRESS':
      case 'ANSWERED':
        if (current.phase === 'dialing' || current.phase === 'ringing') {
          callMachine.connected()
        }
        break

      /**
       * ÉCHEC DE NUMÉROTATION vu par le SERVEUR — il tranche vite (≈4 s sur un
       * numéro invalide) et, lui, connaît la CAUSE. Le poste, de son côté, ne
       * remonte qu'un 31005 anonyme : c'est donc ce push qui porte la raison
       * affichée à l'agent et l'issue proposée en un clic.
       *
       * Les deux annonces décrivent le même fait dans un ordre non garanti :
       * `dialFailed()` classe la première, `refineDialFailure()` enrichit
       * lorsqu'un wrapup est déjà ouvert. Aucune des deux n'écrase l'autre.
       */
      case 'NO_ANSWER':
      case 'BUSY':
      case 'FAILED': {
        // #144 : la cause (code + catégorie) voyage avec le push ; elle enrichit
        // le panneau d'échec sans changer l'issue suggérée.
        const failure = describeDialFailure(event.status, readFailureCause(event))
        if (current.phase === 'dialing' || current.phase === 'ringing') {
          callMachine.dialFailed(failure)
        } else if (current.phase === 'wrapup') {
          callMachine.refineDialFailure(failure)
        } else if (current.phase !== 'idle') {
          callMachine.disconnected({ needsOutcome: true })
        }
        break
      }

      case 'MISSED':
        // Entrant que personne n'a pris (ou pris par un autre agent) : il n'y a
        // rien à qualifier pour NOUS. Sans ce cas, l'écran restait figé sur
        // « Appel entrant » jusqu'à ce que le SDK daigne émettre `cancel` — et
        // la machine, non-idle, refusait l'entrant SUIVANT (famille F-8).
        if (current.phase === 'incoming') {
          callMachine.disconnected({ needsOutcome: false })
        }
        break
    }
  }

  /**
   * Relit l'état serveur de l'appel courant. Ne corrige que les identifiants :
   * la PHASE reste décidée par le SDK local, qui sait seul si le micro est
   * ouvert (la DB, elle, a toujours un train de retard).
   */
  private async resyncCurrentCall(): Promise<void> {
    const { callId } = callMachine.getSnapshot()
    if (!callId) return
    try {
      const snapshot = await telephonyApi.getCall(callId)
      callMachine.syncFromServer({
        callId: snapshot.id,
        twilioSid: snapshot.twilioSid,
      })
    } catch (error) {
      // Un rattrapage raté n'est pas une panne : le prochain `call.updated`
      // fera le travail. On ne programme surtout PAS de retry (interdit).
      console.warn('[telephony-v2] Rattrapage d’état impossible', error)
    }
  }

  disconnect(): void {
    this.socket?.removeAllListeners()
    this.socket?.disconnect()
    this.socket = null
  }
}

export const telephonySocket = new TelephonySocket()
